import { defineComponent } from "vue";
import MessageAlert from "@/components/message-alert";
import type { CreateOrderResponse } from "@/services/sim-tariffs/response/CreateOrderResponse";
import type { SmsVerificationConfirmResponse } from "@/services/sim-tariffs/response/SmsVerificationConfirmResponse";
import type { SmsVerificationInitResponse } from "@/services/sim-tariffs/response/SmsVerificationInitResponse";
import { FormKit } from "@formkit/vue";
import * as Sentry from "@sentry/vue";

const FALLBACK_ERR_MESSAGE =
  "Nastala neočekávaná chyba. Prosím, kontaktujte naši Zákaznickou linku na čísle 730 430 430.";
const INVALID_PHONE_MESSAGE =
  "Zadané telefonní číslo nemá platný formát. Musí obsahovat 9 cifer. První cifra musí být 6 nebo 7.";
const INVALID_CLIENT_NO_MESSAGE =
  "Zadané klientské číslo je neplatné. Pokud se Vám nedaří ověřit Vaše klientské číslo, prosím kontaktujte naši Zákaznickou linku na čísle 730 430 430.";
const INVALID_CODE_MESSAGE =
  "Zadaný ověřovací kód je neplatný. Pokud se Vám nedaří ověřit Vaše klientské číslo, prosím kontaktujte naši Zákaznickou linku na čísle 730 430 430.";
const FORM_NAME = "sim order form: existing client";
const CUSTOMER_TYPE = "existing";

export default defineComponent({
  template: `
      <div class="order-form order-step-1" v-if="!this.stepOne.isPassed">
      <FormKit
          type="form"
          @submit="handleSubmitStepOne"
          :form-class="this.stepOne.isSubmitted ? 'hide' : 'show'"
          submit-label="Zaslat ověřovací SMS"
      >
        <MessageAlert v-if="this.stepOne.errorMessage" type="error">
          <span>{{ this.stepOne.errorMessage }}</span>
        </MessageAlert>

        <FormKit
            v-if="!this.stepOne.isPassed"
            v-model="transferNumber"
            type="checkbox"
            label="Přeji si přenést své současné telefonní číslo"
            help="Chcete k PODĚ přejít se svým stávajícím telefonním číslem? Vyplňte ho prosím zde. "
            name="transfer_number"
            @input="this.handleTransferNumberChange"
        />

        <div v-if="this.transferNumber">
          <FormKit
              type="text"
              name="phone_no_transfer"
              id="phone_no_transfer"
              v-model="this.phoneNoTransfer"
              label="Telefonní číslo k přenosu"
              :validation="this.transferNumber ? 'required' : null"
              @focusout="handleFocusOut"
              :sections-schema="{
            prefix: {
              children: [
                {
                  $el: 'span',
                  attrs: {
                    style: 'margin: 0 0.5rem;',
                  },
                  // eslint-disable-next-line no-irregular-whitespace
                  children: '+420',
                },
              ],
            },
          }"
          />
        </div>

        <div v-if="!this.stepOne.isPassed">
          <FormKit
              type="text"
              name="client_id"
              id="client_id"
              v-model="this.clientId"
              label="Klientské číslo"
              help="Vaše klientské číslo u společnosti PODA. Najdete ho např. na smlouvě či faktuře."
              validation="required|number"
              validation-visibility="submit"
              @focusout="handleFocusOut"
          />
        </div>
      </FormKit>

      <div class="alert alert-info">Pro přenos vašeho současného telefonního čísla nezapomeňte zaškrtnout možnost <strong>Přeji si přenést své současné telefonní číslo</strong>.</div>

      </div>
      <div
          class="order-form order-step-2"
          v-if="this.stepOne.isSubmitted && !this.stepTwo.isSubmitted"
      >
      <MessageAlert v-if="this.stepTwo.errorMessage" type="error">
        <span>{{ this.stepTwo.errorMessage }}</span>
      </MessageAlert>
      <FormKit
          type="form"
          @submit="handleSubmitStepTwo"
          :form-class="this.stepTwo.isSubmitted ? 'hide' : 'show'"
          submit-label="Ověřit"
      >
        <FormKit
            type="text"
            name="sms_code"
            id="sms_code"
            v-model="this.verificationCode"
            label="Ověřovací kód z SMS"
            help="Zpráva by Vám měla dorazit za několik sekund."
            validation="required|number"
            validation-visibility="dirty"
            @focusout="handleFocusOut"
        />
      </FormKit>
      </div>
      <div class="order-form order-step-final" v-if="this.stepTwo.isPassed">
      <MessageAlert
          v-if="!this.stepFinal.isPassed && this.stepFinal.errorMessage === null"
          type="info"
      >
        Vaše objednávka se zpracovává. Moment strpení, prosím.
      </MessageAlert>
      <MessageAlert v-if="this.stepFinal.isPassed" type="success">
        <span>Vaše objednávka byla úspěšně zpracována. Děkujeme!</span>
      </MessageAlert>
      <MessageAlert
          v-if="
        this.stepFinal.isSubmitted &&
        !this.stepFinal.isPassed &&
        this.stepFinal.errorMessage !== null
      "
          type="error"
      >
      <span>
        {{
          this.stepFinal.errorMessage ??
          "Nastala neočekávaná chyba. Prosím, kontaktujte naši Zákaznickou linku na čísle 730 430 430."
        }}
      </span>
      </MessageAlert>
      <div
          v-if="
        this.stepFinal.isSubmitted &&
        this.stepFinal.isPassed &&
        this.stepFinal.errorMessage === null
      "
      >
        <h3 class="mb-3">Objednávka byla dokončena</h3>
        <p>
          Do <a href="https://klient.poda.cz/">Klientské zóny</a> jsme vám připravili smlouvu. Prosím podepište ji, po podpisu smlouvy vám bude odeslána vaše
          nová SIM karta. Nedojde-li k podpisu smlouvy do 10 dní, bude objednávka automaticky stornována.
        </p>
      </div>
      </div>
      <slot></slot>
    `,
  name: "OrderFormExistingClient",
  props: {
    program: {
      type: Object,
      required: true,
    },
    includeIptv: {
      type: Boolean,
      default: false,
      required: false,
    },
    iptvPrice: {
      type: Number,
      default: 0,
      required: false,
    },
    gtmEventPrefix: {
      type: String,
      required: false,
      default: "sim_order_form_",
    },
  },
  emits: ["stepChanged"],
  data() {
    return {
      clientId: null,
      verificationInstanceId: null,
      verificationCode: null,
      verifiedClientId: null,
      orderCreateToken: null,
      transferNumber: false,
      phoneNoContact: null,
      phoneNoTransfer: null,
      // Steps
      stepOne: {
        isSubmitted: false,
        isPassed: false,
        errorMessage: null,
      },
      stepTwo: {
        isSubmitted: false,
        isPassed: false,
        errorMessage: null,
      },
      stepFinal: {
        isSubmitted: false,
        isPassed: false,
        errorMessage: null,
      },
    };
  },
  computed: {
    phoneNoTransferTrimmed() {
      return this.phoneNoTransfer.replace(/\s/g, "");
    },
    phoneNoContactTrimmed() {
      return this.phoneNoContact.replace(/\s/g, "");
    },
  },
  components: {
    MessageAlert,
    FormKit,
  },
  mounted() {
    this.$emit("stepChanged", {
      current: 1,
      total: 3,
    });

    const search = new URLSearchParams(window.location.search);
    this.clientId = search.get("customer");

    this.$store.direct.getters.exponeaService.track("order_form", {
      form_name: FORM_NAME,
      form_state: "opened",
      form_step: 1,
      url: window.location.href,
      selected_tariff_id: this.program.program_id,
      selected_tariff_name: this.getTariffName(),
      selected_tariff_technology: this.getTariffTechnology(),
    });

    this.$store.direct.getters.gtmService.track(this.gtmEventPrefix + "open", {
      existing_customer: CUSTOMER_TYPE,
      sim_tariff: this.getTariffName(),
      technology: this.getTariffTechnology(),
    });
  },
  methods: {
    getTariffTechnology(): string {
      if (this.includeIptv) return "iptv+sim";
      return "sim";
    },
    getTariffName(): string {
      if (this.includeIptv) return "IPTV + " + this.program.name;
      return this.program.name;
    },
    async handleFocusOut(payload: FocusEvent) {
      this.$store.direct.getters.exponeaService.track("order_form", {
        form_name: FORM_NAME,
        form_state: "edited",
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        field_key: payload.target?.name,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        field_value: payload.target?._value,
        url: window.location.href,
        selected_tariff_id: this.program.program_id,
        selected_tariff_name: this.getTariffName(),
        selected_tariff_technology: this.getTariffTechnology(),
      });
    },
    async handleTransferNumberChange() {
      await this.$store.direct.getters.exponeaService.track("order_form", {
        form_name: FORM_NAME,
        form_state: "edited",
        field_key: "transfer_number",
        field_value: this.transferNumber,
        url: window.location.href,
        selected_tariff_id: this.program.program_id,
        selected_tariff_name: this.getTariffName(),
        selected_tariff_technology: this.getTariffTechnology(),
      });
    },
    async handleSubmitStepOne() {
      // Pre-submit check
      const regex = /^[67][0-9]{8}$/;
      if (this.transferNumber && !regex.test(this.phoneNoTransferTrimmed)) {
        this.stepOne.errorMessage = INVALID_PHONE_MESSAGE;
        return;
      }
      // Pre-submit check passed
      let smsInit = {} as SmsVerificationInitResponse;
      try {
        smsInit = await this.$store.direct.getters.simTariffService.smsVerificationInit({
          client_id: parseInt(this.clientId),
        });
      } catch (error) {
        this.stepOne.isSubmitted = false;
        Sentry.captureException(error);
      }
      // Success
      if (smsInit?.code && smsInit?.code === 200) {
        this.verificationInstanceId = smsInit?.data.client.instance_id;
        this.stepOne.isSubmitted = true;
        this.stepOne.isPassed = true;
        this.$emit("stepChanged", {
          current: 2,
          total: 3,
        });
        await this.$store.direct.getters.exponeaService.track("order_form", {
          form_name: FORM_NAME,
          form_state: "step_changed",
          form_step: 2,
          url: window.location.href,
          selected_tariff_id: this.program.program_id,
          selected_tariff_name: this.getTariffName(),
          selected_tariff_technology: this.getTariffTechnology(),
        });
        return;
      }
      // Invalid Client No. format
      if (smsInit?.code && smsInit?.code === 422) {
        this.stepOne.errorMessage = INVALID_CLIENT_NO_MESSAGE;
        await this.$store.direct.getters.exponeaService.track("order_form", {
          form_name: FORM_NAME,
          form_state: "step_change_failed",
          error_message: INVALID_CLIENT_NO_MESSAGE,
          form_step: 1,
          url: window.location.href,
          selected_tariff_id: this.program.program_id,
          selected_tariff_name: this.getTariffName(),
          selected_tariff_technology: this.getTariffTechnology(),
        });
        this.stepOne.isSubmitted = false;
        return;
      }
      // Invalid Client No. value
      else if (smsInit?.code && smsInit?.code === 404) {
        this.stepOne.errorMessage = INVALID_CLIENT_NO_MESSAGE;
        await this.$store.direct.getters.exponeaService.track("order_form", {
          form_name: FORM_NAME,
          form_state: "step_change_failed",
          error_message: INVALID_CLIENT_NO_MESSAGE,
          form_step: 1,
          url: window.location.href,
          selected_tariff_id: this.program.program_id,
          selected_tariff_name: this.getTariffName(),
          selected_tariff_technology: this.getTariffTechnology(),
        });
        this.stepOne.isSubmitted = false;
        return;
      }
      // Any other err
      this.stepOne.errorMessage = FALLBACK_ERR_MESSAGE;
      await this.$store.direct.getters.exponeaService.track("order_form", {
        form_name: FORM_NAME,
        form_state: "step_change_failed",
        error_message: FALLBACK_ERR_MESSAGE,
        form_step: 1,
        url: window.location.href,
        selected_tariff_id: this.program.program_id,
        selected_tariff_name: this.getTariffName(),
        selected_tariff_technology: this.getTariffTechnology(),
      });
      this.stepOne.isSubmitted = false;
    },
    async handleSubmitStepTwo() {
      let smsConfirm = {} as SmsVerificationConfirmResponse;
      try {
        smsConfirm = await this.$store.direct.getters.simTariffService.smsVerificationConfirm({
          client_id: parseInt(this.clientId),
          instance_id: parseInt(this.verificationInstanceId),
          password: this.verificationCode.toString(),
        });
      } catch (error) {
        this.stepTwo.isSubmitted = false;
        Sentry.captureException(error);
      }
      // Success
      if (smsConfirm?.code && smsConfirm?.code === 200) {
        this.stepTwo.isSubmitted = true;
        this.orderCreateToken = smsConfirm?.data?.client.token;
        this.verifiedClientId = this.clientId;
        this.stepTwo.isPassed = true;
        this.$emit("stepChanged", {
          current: 3,
          total: 3,
        });

        await this.$store.direct.getters.exponeaService.identify({
          customer_number: this.verifiedClientId.toString(),
        });

        await this.$store.direct.getters.exponeaService.track("order_form", {
          form_name: FORM_NAME,
          form_state: "step_changed",
          form_step: 3,
          url: window.location.href,
          selected_tariff_id: this.program.program_id,
          selected_tariff_name: this.getTariffName(),
          selected_tariff_technology: this.getTariffTechnology(),
        });
        await this.handleSubmitStepFinal();
        return;
      }
      // Invalid Code format
      if (smsConfirm?.code && smsConfirm?.code === 422) {
        this.stepTwo.errorMessage = INVALID_CODE_MESSAGE;
        await this.$store.direct.getters.exponeaService.track("order_form", {
          form_name: FORM_NAME,
          form_state: "step_change_failed",
          error_message: INVALID_CODE_MESSAGE,
          form_step: 2,
          url: window.location.href,
          selected_tariff_id: this.program.program_id,
          selected_tariff_name: this.getTariffName(),
          selected_tariff_technology: this.getTariffTechnology(),
        });
        this.stepTwo.isSubmitted = false;
        return;
      }
      // Invalid Code value
      else if (smsConfirm?.code && smsConfirm?.code === 401) {
        this.stepTwo.errorMessage = INVALID_CODE_MESSAGE;
        await this.$store.direct.getters.exponeaService.track("order_form", {
          form_name: FORM_NAME,
          form_state: "step_change_failed",
          error_message: INVALID_CODE_MESSAGE,
          form_step: 2,
          url: window.location.href,
          selected_tariff_id: this.program.program_id,
          selected_tariff_name: this.getTariffName(),
          selected_tariff_technology: this.getTariffTechnology(),
        });
        this.stepTwo.isSubmitted = false;
        return;
      }
      // Any other err
      this.stepTwo.errorMessage = FALLBACK_ERR_MESSAGE;
      await this.$store.direct.getters.exponeaService.track("order_form", {
        form_name: FORM_NAME,
        form_state: "step_change_failed",
        error_message: FALLBACK_ERR_MESSAGE,
        form_step: 2,
        url: window.location.href,
        selected_tariff_id: this.program.program_id,
        selected_tariff_name: this.getTariffName(),
        selected_tariff_technology: this.getTariffTechnology(),
      });
      this.stepTwo.isSubmitted = false;
    },
    async handleSubmitStepFinal() {
      let submitResponse = {} as CreateOrderResponse;
      try {
        this.stepFinal.errorMessage = null;
        this.stepFinal.isSubmitted = true;
        const submitRequest = {
          token: this.orderCreateToken ?? "",
          client_id: this.verifiedClientId ?? 0,
          program_id: this.program.program_id ?? 0,
          transfer_phone: this.transferNumber ?? false,
          instance_id: this.verificationInstanceId ?? 0,
          phone_number: undefined as number,
        };
        if (this.transferNumber) {
          submitRequest.phone_number = this.phoneNoTransferTrimmed;
        }
        submitResponse = await this.$store.direct.getters.simTariffService.createOrder(submitRequest);
      } catch (error) {
        this.stepFinal.errorMessage = FALLBACK_ERR_MESSAGE;
        Sentry.captureException(error);
      }
      // Success
      if (submitResponse?.code && submitResponse?.code === 200) {
        await this.$store.direct.getters.exponeaService.track("order_form", {
          form_name: FORM_NAME,
          form_state: "submitted",
          form_step: 3,
          url: window.location.href,
          selected_tariff_id: this.program.program_id,
          selected_tariff_name: this.getTariffName(),
          selected_tariff_technology: this.getTariffTechnology(),
          transfer_phone: this.transferNumber ?? false,
          phone_number: this.transferNumber ? this.phoneNoTransferTrimmed : "",
        });
        await this.$store.direct.getters.gtmService.track(this.gtmEventPrefix + "submit", {
          existing_customer: CUSTOMER_TYPE,
          sim_tariff: this.getTariffName(),
          technology: this.getTariffTechnology(),
        });
        if (typeof sessionStorage !== undefined && typeof sessionStorage.setItem === "function") {
          sessionStorage.setItem("OrderFormExistingClient.stepThree.lastSubmittedAt", new Date().getTime().toString());
        }
        this.stepFinal.isPassed = true;
        return;
      }
      await this.$store.direct.getters.gtmService.track(this.gtmEventPrefix + "submit_failed", {
        existing_customer: CUSTOMER_TYPE,
        sim_tariff: this.getTariffName(),
        technology: this.getTariffTechnology(),
      });
      // Invalid Code format
      if (submitResponse?.code && submitResponse?.code === 422) {
        await this.$store.direct.getters.exponeaService.track("order_form", {
          form_name: FORM_NAME,
          form_state: "submit_failed",
          error_message: INVALID_PHONE_MESSAGE,
          form_step: 3,
          url: window.location.href,
          selected_tariff_id: this.program.program_id,
          selected_tariff_name: this.getTariffName(),
          selected_tariff_technology: this.getTariffTechnology(),
        });
        this.stepFinal.errorMessage = INVALID_PHONE_MESSAGE;
        return;
      }
      // Any other err
      await this.$store.direct.getters.exponeaService.track("order_form", {
        form_name: FORM_NAME,
        form_state: "submit_failed",
        error_message: FALLBACK_ERR_MESSAGE,
        form_step: 3,
        url: window.location.href,
        selected_tariff_id: this.program.program_id,
        selected_tariff_name: this.getTariffName(),
        selected_tariff_technology: this.getTariffTechnology(),
      });
      this.stepFinal.errorMessage = FALLBACK_ERR_MESSAGE;
    },
  },
});
